import BaseWidgetController from './base_widget_controller';
import {
  Chart,
  ScatterController,
  PointElement,
  LineController,
  LineElement,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { SLATE_500 } from '../../../shared/colors';

Chart.register(
  ScatterController,
  PointElement,
  LineController,
  LineElement,
  LinearScale,
  Tooltip
);

/**
 * Base controller for scatter chart widgets on the dashboard.
 * Extends BaseWidgetController to add Chart.js scatter plot rendering.
 *
 * Child controllers should:
 * 1. Override `getScatterConfig(data)` to return { datasets, xLabel, yLabel }
 * 2. Define value targets for axis labels
 */
export default class extends BaseWidgetController {
  static targets = [...BaseWidgetController.targets, 'chart'];

  connect() {
    super.connect();
    this.chart = null;
  }

  disconnect() {
    super.disconnect();
    this.destroyChart();
  }

  showLoading() {
    super.showLoading();

    if (this.hasChartTarget) {
      this.chartTarget.classList.add('opacity-50');
    }
  }

  hideLoading() {
    super.hideLoading();

    if (this.hasChartTarget) {
      this.chartTarget.classList.remove('opacity-50');
    }
  }

  renderData(data) {
    super.renderData(data);
    this.renderChart(data);
  }

  renderChart(data) {
    if (!this.hasChartTarget) return;

    this.destroyChart();

    const config = this.getScatterConfig(data);
    if (!config) return;

    const { datasets, xLabel, yLabel, xMax, yMax } = config;
    const ctx = this.chartTarget.getContext('2d');

    this.chart = new Chart(ctx, {
      type: 'scatter',
      data: { datasets },
      options: this.getChartOptions(xLabel, yLabel, xMax, yMax),
    });
  }

  getChartOptions(xLabel, yLabel, xMax, yMax) {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context) => this.formatTooltipLabel(context),
          },
        },
      },
      scales: {
        x: {
          type: 'linear',
          position: 'bottom',
          beginAtZero: true,
          suggestedMax: xMax * 1.1,
          title: {
            display: true,
            text: xLabel,
            color: SLATE_500,
            font: { size: 12 },
          },
          grid: {
            display: false,
          },
          ticks: {
            color: SLATE_500,
            font: { size: 12 },
            callback: (value) => this.formatNumber(value),
          },
        },
        y: {
          type: 'linear',
          beginAtZero: true,
          suggestedMax: Math.min(yMax * 1.1, 100),
          title: {
            display: true,
            text: yLabel,
            color: SLATE_500,
            font: { size: 12 },
          },
          grid: {
            display: false,
          },
          ticks: {
            color: SLATE_500,
            font: { size: 12 },
            callback: (value) => `${value}%`,
          },
        },
      },
    };
  }

  /**
   * Override in child controllers to customize tooltip display.
   */
  formatTooltipLabel(context) {
    const point = context.raw;
    if (point.name) {
      return `${point.name}: ${this.formatNumber(point.x)} interactions, ${point.y}%`;
    }
    return `${this.formatNumber(point.x)}, ${point.y}%`;
  }

  /**
   * Override in child controllers to provide scatter data.
   * Must return: { datasets: Array, xLabel: string, yLabel: string, xMax: number, yMax: number }
   */
  getScatterConfig(_data) {
    throw new Error('Child controllers must implement getScatterConfig(data)');
  }

  destroyChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
